/**
 * UserRoleSelect component for changing user roles
 * Feature: 001-user-role-management (Phase 5 - T090, T093-T095)
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { userService } from '../../services/userService';
import type { UserRole, UserOrInvitation } from '../../types/user';

interface UserRoleSelectProps {
  user: UserOrInvitation;
}

export function UserRoleSelect({ user }: UserRoleSelectProps) {
  const [selectedRole, setSelectedRole] = useState<UserRole>(user.role);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingRole, setPendingRole] = useState<UserRole | null>(null);

  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  // Mutation for updating user role
  const updateRoleMutation = useMutation({
    mutationFn: (newRole: UserRole) => {
      return userService.updateUserRole(getToken, user.id, { role: newRole });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowConfirmation(false);
      setPendingRole(null);
    },
    onError: (error: any) => {
      // Reset to original role on error
      setSelectedRole(user.role);
      setPendingRole(null);
      setShowConfirmation(false);
    },
  });

  const handleRoleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newRole = event.target.value as UserRole;
    if (newRole !== selectedRole) {
      setPendingRole(newRole);
      setShowConfirmation(true);
    }
  };

  const confirmRoleChange = () => {
    if (pendingRole) {
      setSelectedRole(pendingRole);
      updateRoleMutation.mutate(pendingRole);
    }
  };

  const cancelRoleChange = () => {
    setPendingRole(null);
    setShowConfirmation(false);
  };

  return (
    <div className="relative">
      <select
        value={selectedRole}
        onChange={handleRoleChange}
        disabled={updateRoleMutation.isPending}
        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <option value="admin">Admin</option>
        <option value="user">User</option>
      </select>

      {/* T094: Confirmation Dialog */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Confirm Role Change
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to change <strong>{user.email}</strong>'s role from{' '}
              <strong>{selectedRole}</strong> to <strong>{pendingRole}</strong>?
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>Warning:</strong> Role changes take effect immediately. The user's access
                permissions will be updated on their next request.
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={cancelRoleChange}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRoleChange}
                disabled={updateRoleMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updateRoleMutation.isPending ? 'Updating...' : 'Confirm Change'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* T095: Error Message */}
      {updateRoleMutation.isError && (
        <div className="absolute top-full left-0 right-0 mt-1 p-2 bg-red-50 border border-red-200 rounded-md z-10">
          <p className="text-sm text-red-800">
            {updateRoleMutation.error instanceof Error
              ? updateRoleMutation.error.message
              : 'Failed to update role. Please try again.'}
          </p>
        </div>
      )}
    </div>
  );
}
